import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import api from '../../../api/client.ts'
import { AuthContext } from '../../../context/AuthContext'
import { getErrorMessage } from '../../../utils/account.ts'
import { getTopUpConfig, buyTopUp, computeTokens, bonusFor, clampAmount, initialAmount, amountForTokens, type TopUpConfig } from '../../../api/topup.ts'
import { clearStoredPromo, type PromoPurchase } from '../../../api/promo.ts'
import { usePromoCode } from '../../../hooks/usePromoCode'
import PromoCodeField from '../../../components/PromoCodeField'
import { useAuth } from '../../../context/AuthContext'
import { reachGoal } from '../../../utils/ym'
import { markPaymentStart } from '../../../utils/paymentReturn.ts'

interface Props {
	open: boolean
	onClose: () => void
	/** Сколько токенов не хватает (плашка нехватки): ползунок сразу встаёт на сумму, которая их покрывает. */
	needTokens?: number | null
	/** Открыто из плашки нехватки: после оплаты баланс перечитывается сразу, а «Готово» оставляет на странице — там ждёт
	 *  то, на что пополняли (кнопка запуска, «Посмотреть ответ»), — а не уводит в кабинет. */
	stayOnPage?: boolean
}

type Step = 'config' | 'qr' | 'success' | 'error' | 'timeout'

interface PaymentStatusResponse {
	order_id: string
	status: string
	amount: number
	credits: number
}

export default function TopUpSlider({ open, onClose, needTokens = null, stayOnPage = false }: Props) {
	const navigate = useNavigate()
	// После оплаты из плашки — сверка баланса сразу (с уведомлением «Баланс пополнен», как у опроса раз в 15 с)
	const auth = useContext(AuthContext)
	const syncBalance = auth?.syncBalance ?? auth?.refreshUser
	const [cfg, setCfg] = useState<TopUpConfig | null>(null)
	const [amount, setAmount] = useState(190)
	const [step, setStep] = useState<Step>('config')
	const [paymentUrl, setPaymentUrl] = useState('')
	const [creditsAdded, setCreditsAdded] = useState(0)
	const [errorMsg, setErrorMsg] = useState('')
	const [busy, setBusy] = useState(false)
	// Сумма созданного платежа (со скидкой) — для QR-экрана
	const [payAmount, setPayAmount] = useState<number | null>(null)
	const { refreshUser } = useAuth()

	// Промокод на докупку: перепроверка с паузой, пока тянут слайдер —
	// фиксированная скидка действует не на любую сумму.
	const promoPurchase = useMemo<PromoPurchase | null>(
		() => (open && cfg && step === 'config' ? { purchase_kind: 'topup', amount: clampAmount(amount, cfg) } : null),
		[open, cfg, step, amount],
	)
	const promo = usePromoCode(promoPurchase, { debounceMs: 400, onActivated: () => refreshUser() })
	const { reset: resetPromo } = promo
	const promoQuote = promo.quote

	const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const pollingStartRef = useRef(0)

	const stopPolling = useCallback(() => {
		if (pollingRef.current) {
			clearInterval(pollingRef.current)
			pollingRef.current = null
		}
	}, [])

	const startPolling = useCallback((oid: string) => {
		stopPolling()
		pollingStartRef.current = Date.now()
		pollingRef.current = setInterval(async () => {
			if (Date.now() - pollingStartRef.current > 300000) {
				stopPolling()
				setStep('timeout')
				return
			}
			try {
				const res = await api.request<PaymentStatusResponse>(`/api/v1/payments/status/${oid}`)
				if (res.status === 'completed') {
					stopPolling()
					setCreditsAdded(res.credits)
					setStep('success')
					if (stayOnPage) void syncBalance?.()
				} else if (res.status === 'cancelled' || res.status === 'expired' || res.status === 'failed') {
					stopPolling()
					setErrorMsg(res.status === 'cancelled' ? 'Платёж отклонён. Попробуйте другой способ оплаты.' : 'Время оплаты истекло. Попробуйте снова.')
					setStep('error')
				}
			} catch { /* ignore */ }
		}, 3000)
	}, [stopPolling, stayOnPage, syncBalance])

	// Конфиг перечитываем на КАЖДОЕ открытие, а не один раз за жизнь вкладки:
	// компонент смонтирован постоянно (HeaderActions рендерит его всегда), и
	// закэшированный курс через час показывал бы одну цену, а начислялось бы
	// по новой. Отсюда и зависимости только open и needTokens (плашка фиксирует
	// его в момент нажатия): cfg в теле не читается, иначе setCfg зациклил бы эффект.
	useEffect(() => {
		if (!open) return
		getTopUpConfig()
			.then((c) => {
				setCfg(c)
				setAmount(needTokens && needTokens > 0 ? amountForTokens(needTokens, c) : initialAmount(c))
				// Код из ссылки ?promo= — сразу в поле и на проверку
				resetPromo()
			})
			.catch((e) => {
				setErrorMsg(getErrorMessage(e, 'Не удалось загрузить параметры'))
				setStep('error')
			})
	}, [open, needTokens, resetPromo])

	useEffect(() => () => stopPolling(), [stopPolling])

	// Escape — до слоёв под окном (мини-чат помощников, шторки ловят его на window): закрывает только окно и только
	// на выборе суммы, как клик по фону; во время оплаты не закрывает, но и вниз не пропускает.
	const escRef = useRef<() => void>(() => {})
	useEffect(() => {
		if (!open) return
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') return
			e.stopPropagation()
			escRef.current()
		}
		window.addEventListener('keydown', onKey, true)
		return () => window.removeEventListener('keydown', onKey, true)
	}, [open])

	if (!open) return null

	const tokens = cfg ? computeTokens(amount, cfg) : 0
	const bonus = cfg ? Math.round(bonusFor(amount, cfg) * 100) : 0
	const amtPct = cfg ? ((amount - cfg.min_amount) / (cfg.max_amount - cfg.min_amount)) * 100 : 0

	const handleClose = () => {
		stopPolling()
		setStep('config')
		setPaymentUrl('')
		setErrorMsg('')
		setBusy(false)
		setPayAmount(null)
		onClose()
	}
	escRef.current = () => { if (!busy && step === 'config') handleClose() }

	const handleBuy = async () => {
		if (!cfg || busy || promo.pending) return
		const promoCode = promoQuote?.code ?? null
		reachGoal('topup_sbp', { amount: promoQuote?.price_after ?? clampAmount(amount, cfg) })
		setBusy(true)
		setErrorMsg('')
		try {
			const res = await buyTopUp(clampAmount(amount, cfg), 'sbp', promoCode)
			if (promoCode) clearStoredPromo(promoCode)
			setPayAmount(typeof res.amount === 'number' ? res.amount : null)
			const url = res.payment_url
			// Прямая СБП-ссылка (её распознаёт банк) → показываем QR сами;
			// иначе редиректим на хостед-страницу FreeKassa, которая сама рисует СБП-QR
			// (иначе банк-камера видит адрес сайта и ругается на внешнюю ссылку).
			if (/qr\.nspk\.ru/i.test(url)) {
				setPaymentUrl(url)
				setStep('qr')
				startPolling(res.order_id)
			} else {
				markPaymentStart({ balanceBefore: auth?.user?.credits_balance ?? 0, tariffBefore: null, tariffWanted: null })
				window.location.href = url
			}
		} catch (e) {
			setErrorMsg(getErrorMessage(e, 'Не удалось создать платёж'))
			setStep('error')
		} finally {
			setBusy(false)
		}
	}

	return (
		<div className="sub-modal-overlay" onClick={() => !busy && step === 'config' && handleClose()}>
			<div className="sub-modal topup-modal" onClick={(e) => e.stopPropagation()}>
				{step === 'config' && (
					<>
						<h3 className="sub-modal__title">Докупить токены</h3>
						{/* Только про ПОКУПКУ, без условий хранения (решение владельца
						    2026-08-07): срок жизни докупки зависит от сценария — без
						    подписки лот сгорает через 30 дней, у подписчика остаток
						    участвует в переносе (carryover). Сроки юзер видит в
						    «Ваши токены». */}
						<p className="sub-modal__text">Разовое пополнение — токены сразу добавятся к балансу.</p>

						{!cfg ? (
							<div className="sub-packages-loading"><div className="spinner" /></div>
						) : (
							<>
								{/* Бонусы можно выключить в админке целиком — тогда блок
								    не рисуем вовсе, а не показываем «+0%». */}
								{bonus > 0 && (
									<div className="topup-bonus">
										<span className="topup-bonus__plus">+{bonus}%</span>
										<span className="topup-bonus__label">бонусных токенов</span>
									</div>
								)}

								<div className="topup-readout">
									<div className="topup-readout__tokens">{tokens.toLocaleString('ru-RU')} токенов</div>
									<div className="topup-readout__price">
										{promoQuote && promoQuote.discount_rub > 0 ? (
											<>
												<s className="topup-readout__old">{amount.toLocaleString('ru-RU')} ₽</s>
												{promoQuote.price_after.toLocaleString('ru-RU')} ₽
											</>
										) : (
											<>{amount.toLocaleString('ru-RU')} ₽</>
										)}
									</div>
								</div>
								{promoQuote && promoQuote.bonus_credits > 0 && (
									<div className="topup-promo-bonus">
										+{promoQuote.bonus_credits.toLocaleString('ru-RU')} токенов по промокоду
									</div>
								)}

								<div className="ctx-btn__slider-wrap topup-slider-wrap">
									<input
										type="range"
										className="context-slider-input"
										min={cfg.min_amount}
										max={cfg.max_amount}
										step={cfg.step}
										value={amount}
										onChange={(e) => setAmount(clampAmount(parseInt(e.target.value), cfg))}
										style={{ '--progress': `${amtPct}%` } as React.CSSProperties}
									/>
								</div>

								{cfg.presets.length > 0 && (
								<div className="topup-presets">
									{cfg.presets.map((p) => (
										<button
											key={p}
											type="button"
											className={`topup-preset ${amount === p ? 'topup-preset--active' : ''}`}
											onClick={() => setAmount(clampAmount(p, cfg))}
										>
											{p.toLocaleString('ru-RU')} ₽
										</button>
									))}
								</div>
								)}

								<PromoCodeField promo={promo} disabled={busy} />

								<div className="topup-pay topup-pay--single">
									<button className="sub-btn primary topup-pay__btn" type="button" disabled={busy || promo.pending} onClick={handleBuy}>
										<span>Оплатить</span>
									</button>
								</div>

								<div className="sub-modal__actions">
									<button className="sub-btn" type="button" onClick={handleClose} disabled={busy}>Закрыть</button>
								</div>
							</>
						)}
					</>
				)}

				{step === 'qr' && (
					<>
						<h3 className="sub-modal__title">Оплата</h3>
						<p className="pay-qr-hint">Отсканируйте QR-код камерой телефона или перейдите к оплате</p>
						<div className="pay-qr-row">
							<div className="wrapper">
								<div className="pay-qr-info">
									<div className="pay-qr-provider">Оплата через СБП</div>
									<div className="pay-qr-amount">{(payAmount ?? amount).toLocaleString('ru-RU')} ₽</div>
								</div>
								<div className="pay-qr-status">
									<div className="pay-qr-spinner" />
									<span>Ожидание оплаты</span>
								</div>
							</div>
							<div className="pay-qr-wrapper">
								<QRCodeSVG value={paymentUrl} size={125} bgColor="#ffffff" fgColor="#000000" level="M" includeMargin={false} />
							</div>
						</div>
						<a href={paymentUrl} target="_blank" rel="noopener noreferrer" className="pay-modal-btn primary">Перейти к оплате</a>
						<button className="pay-modal-link" onClick={handleClose}>Отменить</button>
					</>
				)}

				{step === 'success' && (
					<>
						<div className="pay-modal-icon success">
							<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2">
								<circle cx="12" cy="12" r="10" /><polyline points="8 12 11 15 16 9" />
							</svg>
						</div>
						<h3 className="sub-modal__title">Оплата получена!</h3>
						<p className="pay-modal-text">На ваш баланс начислено <strong>{creditsAdded.toLocaleString('ru-RU')}</strong> токенов</p>
						<button className="pay-modal-btn primary" onClick={() => { handleClose(); if (!stayOnPage) navigate('/account') }}>Готово</button>
					</>
				)}

				{step === 'error' && (
					<>
						<div className="pay-modal-icon error">
							<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2">
								<circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
							</svg>
						</div>
						<h3 className="sub-modal__title">Ошибка</h3>
						<p className="pay-modal-text">{errorMsg}</p>
						<button className="pay-modal-btn primary" onClick={() => setStep('config')}>Попробовать снова</button>
						<button className="pay-modal-btn secondary" onClick={handleClose}>Закрыть</button>
					</>
				)}

				{step === 'timeout' && (
					<>
						<div className="pay-modal-icon error">
							<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2">
								<circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
							</svg>
						</div>
						<h3 className="sub-modal__title">Время ожидания истекло</h3>
						<p className="pay-modal-text">Платёж не получен за 5 минут. Если вы оплатили — токены начислятся автоматически.</p>
						<button className="pay-modal-btn primary" onClick={() => setStep('config')}>Попробовать снова</button>
						<button className="pay-modal-btn secondary" onClick={handleClose}>Закрыть</button>
					</>
				)}
			</div>
		</div>
	)
}
